/**
 * Pinia store for the new boost-core campaigns (plural — coexists with V1's
 * useCampaignStore which still drives the legacy /campaign pages via BS2 backend).
 *
 * Will replace useCampaignStore once /campaign/* is deprecated.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import * as api from '@/api/campaignsApi'
import type {
  CallSession,
  CallSessionStatus,
  CampaignDetail,
  CampaignWithCounters,
  CreateCampaignPayload,
  PaginatedCampaigns,
  SearchCampaignsParams,
  UpdateCampaignPayload,
} from '@/types/campaigns'

export const useCampaignsStore = defineStore('campaigns-bc', () => {
  const privateCampaigns = ref<CampaignWithCounters[]>([])
  const managerOwnedCampaigns = ref<CampaignWithCounters[]>([])
  const managerParticipantCampaigns = ref<CampaignWithCounters[]>([])

  const currentDetail = ref<CampaignDetail | null>(null)
  const currentSessions = ref<CallSession[]>([])

  const loading = ref(false)
  const error = ref<string | null>(null)

  async function run<T>(fn: () => Promise<T>): Promise<T> {
    loading.value = true
    error.value = null
    try {
      return await fn()
    } catch (err) {
      error.value = err instanceof Error ? err.message : String(err)
      throw err
    } finally {
      loading.value = false
    }
  }

  // -- Lists -----------------------------------------------------------------

  async function fetchPrivate(params: SearchCampaignsParams = {}): Promise<PaginatedCampaigns> {
    return run(async () => {
      const res = await api.listPrivate(params)
      privateCampaigns.value = res.data
      return res
    })
  }

  async function fetchManagerOwned(params: SearchCampaignsParams = {}): Promise<PaginatedCampaigns> {
    return run(async () => {
      const res = await api.listManagerOwned(params)
      managerOwnedCampaigns.value = res.data
      return res
    })
  }

  async function fetchManagerAsParticipant(
    params: SearchCampaignsParams = {},
  ): Promise<PaginatedCampaigns> {
    return run(async () => {
      const res = await api.listManagerAsParticipant(params)
      managerParticipantCampaigns.value = res.data
      return res
    })
  }

  // -- Detail ----------------------------------------------------------------

  async function fetchDetail(uuid: string): Promise<CampaignDetail> {
    return run(async () => {
      const detail = await api.getDetails(uuid)
      currentDetail.value = detail
      return detail
    })
  }

  async function fetchSessions(uuid: string): Promise<CallSession[]> {
    return run(async () => {
      const sessions = await api.listSessions(uuid)
      currentSessions.value = sessions
      return sessions
    })
  }

  // -- Mutations -------------------------------------------------------------

  async function createCampaign(payload: CreateCampaignPayload): Promise<CampaignWithCounters> {
    return run(() => api.createCampaign(payload))
  }

  async function updateCampaign(
    uuid: string,
    payload: UpdateCampaignPayload,
  ): Promise<CampaignWithCounters> {
    return run(async () => {
      const updated = await api.update(uuid, payload)
      if (currentDetail.value?.uuid === uuid) {
        currentDetail.value = { ...currentDetail.value, ...updated }
      }
      return updated
    })
  }

  async function deleteCampaign(uuid: string): Promise<void> {
    return run(() => api.remove(uuid))
  }

  async function archiveCampaign(uuid: string): Promise<CampaignWithCounters> {
    return run(async () => {
      const updated = await api.archive(uuid)
      if (currentDetail.value?.uuid === uuid) {
        currentDetail.value = { ...currentDetail.value, ...updated }
      }
      return updated
    })
  }

  async function unarchiveCampaign(uuid: string): Promise<CampaignWithCounters> {
    return run(async () => {
      const updated = await api.unarchive(uuid)
      if (currentDetail.value?.uuid === uuid) {
        currentDetail.value = { ...currentDetail.value, ...updated }
      }
      return updated
    })
  }

  // -- Sessions --------------------------------------------------------------

  async function markSessionStatus(
    sessionUuid: string,
    status: CallSessionStatus,
  ): Promise<CallSession> {
    return run(async () => {
      const updated = await api.updateSession(sessionUuid, { status })
      const idx = currentSessions.value.findIndex((s) => s.uuid === sessionUuid)
      if (idx !== -1) currentSessions.value[idx] = { ...currentSessions.value[idx], ...updated }
      return updated
    })
  }

  async function deleteSession(sessionUuid: string): Promise<void> {
    return run(async () => {
      await api.deleteSession(sessionUuid)
      currentSessions.value = currentSessions.value.filter((s) => s.uuid !== sessionUuid)
    })
  }

  // -- Computed --------------------------------------------------------------

  const sessionsDone = computed(
    () => currentSessions.value.filter((s) => s.status === 'DONE').length,
  )
  const sessionsPending = computed(
    () => currentSessions.value.filter((s) => s.status === 'PENDING').length,
  )

  function reset() {
    privateCampaigns.value = []
    managerOwnedCampaigns.value = []
    managerParticipantCampaigns.value = []
    currentDetail.value = null
    currentSessions.value = []
    error.value = null
  }

  return {
    privateCampaigns,
    managerOwnedCampaigns,
    managerParticipantCampaigns,
    currentDetail,
    currentSessions,
    loading,
    error,
    sessionsDone,
    sessionsPending,
    fetchPrivate,
    fetchManagerOwned,
    fetchManagerAsParticipant,
    fetchDetail,
    fetchSessions,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    archiveCampaign,
    unarchiveCampaign,
    markSessionStatus,
    deleteSession,
    reset,
  }
})
